import React, { createContext, useEffect, useState, ReactNode } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { syncUser, UserProfile } from '../services/api';
import { trackEvent, trackUserAction } from '../utils/analytics';

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  profileLoading: boolean;
  needsOnboarding: boolean;
  isAuthenticated: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  resendVerificationEmail: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  syncUserProfile: (firebaseUser?: User | null) => Promise<UserProfile | null>;
  completeOnboarding: (profileData: UserProfile) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [authStateStable, setAuthStateStable] = useState(false);

  // Sign in with email and password
  const signInWithEmail = async (email: string, password: string): Promise<void> => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Force token refresh to get latest emailVerified status
      await result.user.reload();
      const refreshedUser = auth.currentUser;
      
      // Check if email is verified (use refreshed user)
      if (!refreshedUser?.emailVerified) {
        console.log('⚠️ Email not verified - signing user out');
        await signOut(auth);
        
        // Track email not verified
        trackEvent('login_blocked', 'authentication', 'email_not_verified');
        trackUserAction('login_blocked', { method: 'email', reason: 'email_not_verified' });
        
        // Throw specific error for unverified email
        const verificationError = new Error('Email not verified. Please check your email and verify your account before logging in.') as Error & { code: string };
        verificationError.code = 'auth/email-not-verified';
        throw verificationError;
      }
      
      // Track successful login
      trackEvent('login_success', 'authentication', 'email_password');
      trackUserAction('login_completed', { method: 'email' });
    } catch (error) {
      // Track failed login (if not already tracked)
      const firebaseError = error as { code?: string };
      if (firebaseError.code !== 'auth/email-not-verified') {
        trackEvent('login_failure', 'authentication', 'email_password');
        trackUserAction('login_failed', { method: 'email', error: error instanceof Error ? error.message : 'unknown' });
      }
      
      console.error('Error signing in with email:', error);
      throw error;
    }
  };

  // Sign up with email and password
  const signUpWithEmail = async (email: string, password: string, displayName: string): Promise<void> => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      // Update the user profile with display name
      await updateProfile(result.user, { displayName });
      
      // Send email verification
      console.log('📧 Sending verification email...');
      await sendEmailVerification(result.user, {
        url: window.location.origin, // Redirect URL after verification
        handleCodeInApp: false,
      });
      console.log('✅ Verification email sent successfully');
      
      // Sign out the user immediately after registration
      // They must verify their email before they can log in
      // Backend sync and Terms acceptance will happen on first login
      console.log('🔒 Signing out user - email verification required');
      await signOut(auth);
      
      // Track successful registration
      trackEvent('registration_success', 'authentication', 'email_password');
      trackUserAction('registration_completed', { method: 'email', displayName, emailVerificationSent: true });
    } catch (error) {
      // Track failed registration
      trackEvent('registration_failure', 'authentication', 'email_password');
      trackUserAction('registration_failed', { method: 'email', error: error instanceof Error ? error.message : 'unknown' });
      
      console.error('Error signing up with email:', error);
      throw error;
    }
  };

  // Sign in with Google
  const signInWithGoogle = async (): Promise<void> => {
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });

      console.log('🔄 Attempting Google Sign-in with popup...');
      const result = await signInWithPopup(auth, provider);

      // Determine if this is a new user or returning user
      const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;

      if (isNewUser) {
        trackEvent('registration_success', 'authentication', 'google');
        trackUserAction('registration_completed', { method: 'google' });
      } else {
        trackEvent('login_success', 'authentication', 'google');
        trackUserAction('login_completed', { method: 'google' });
      }
      
      console.log('✅ Google Sign-in successful');
    } catch (error) {
      const err = error as { code?: string; message?: string };
      const code = err?.code || '';

      console.log('⚠️ Google Sign-in popup failed:', code);

      // Use redirect fallback for various popup issues
      if (code === 'auth/popup-blocked' || 
          code === 'auth/operation-not-supported-in-this-environment' ||
          code === 'auth/cancelled-popup-request') {
        
        console.log('🔄 Attempting Google Sign-in with redirect fallback...');
        try {
          const provider = new GoogleAuthProvider();
          provider.setCustomParameters({ prompt: 'select_account' });
          await signInWithRedirect(auth, provider);
          console.log('🔄 Redirect initiated - waiting for redirect completion...');
          return; // Navigation will occur; onAuthStateChanged will handle post-login
        } catch (redirectError) {
          trackEvent('google_auth_failure', 'authentication', 'google_redirect');
          trackUserAction('google_auth_failed', { error: redirectError instanceof Error ? redirectError.message : 'unknown' });
          console.error('❌ Error signing in with Google (redirect):', redirectError);
          throw redirectError;
        }
      }

      // Handle popup closed by user - offer redirect option
      if (code === 'auth/popup-closed-by-user') {
        console.log('ℹ️ User closed the popup - offering redirect alternative...');
        
        // Try redirect as fallback
        try {
          const provider = new GoogleAuthProvider();
          provider.setCustomParameters({ prompt: 'select_account' });
          await signInWithRedirect(auth, provider);
          console.log('🔄 Redirect initiated after popup closure...');
          return;
        } catch (redirectError) {
          console.error('❌ Redirect fallback also failed:', redirectError);
          // Don't throw error for popup closure, just return silently
          return;
        }
      }

      // Track failed Google authentication for other errors
      trackEvent('google_auth_failure', 'authentication', 'google_popup');
      trackUserAction('google_auth_failed', { error: error instanceof Error ? error.message : 'unknown' });
      console.error('❌ Error signing in with Google:', error);
      throw error;
    }
  };

  // Send password reset email
  const sendPasswordReset = async (email: string): Promise<void> => {
    try {
      console.log('📧 Sending password reset email to:', email);
      await sendPasswordResetEmail(auth, email, {
        url: window.location.origin, // Redirect URL after password reset
        handleCodeInApp: false,
      });
      console.log('✅ Password reset email sent successfully');
      
      // Track password reset request
      trackEvent('password_reset_requested', 'authentication', 'email');
      trackUserAction('password_reset_email_sent', { email });
    } catch (error) {
      console.error('❌ Error sending password reset email:', error);
      
      // Track failed password reset
      trackEvent('password_reset_failed', 'authentication', 'email');
      trackUserAction('password_reset_failed', { email, error: error instanceof Error ? error.message : 'unknown' });
      
      throw error;
    }
  };

  // Resend verification email for existing unverified users
  const resendVerificationEmail = async (email: string, password: string): Promise<void> => {
    try {
      console.log('📧 Resending verification email to:', email);
      
      // Sign in the user temporarily to send verification email
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Send verification email
      await sendEmailVerification(result.user, {
        url: window.location.origin,
        handleCodeInApp: false,
      });
      
      console.log('✅ Verification email resent successfully');
      
      // Sign out the user again
      await signOut(auth);
      console.log('🔒 User signed out after resending verification email');
      
      // Track verification email resent
      trackEvent('verification_email_resent', 'authentication', 'email');
      trackUserAction('verification_email_resent', { email });
    } catch (error) {
      console.error('❌ Error resending verification email:', error);
      
      // Track failed resend
      trackEvent('verification_email_resend_failed', 'authentication', 'email');
      trackUserAction('verification_email_resend_failed', { email, error: error instanceof Error ? error.message : 'unknown' });
      
      throw error;
    }
  };

  // Logout
  const logout = async (): Promise<void> => {
    try {
      // Track logout before signing out
      trackEvent('logout', 'authentication', 'user_initiated');
      trackUserAction('logout_completed');
      
      // Logout from Firebase
      if (user) {
        await signOut(auth);
      }
      
      // Clear all auth state
      setUserProfile(null);
      setNeedsOnboarding(false);
      
      console.log('✅ Logout successful');
    } catch (error) {
      console.error('❌ Error signing out:', error);
      // Even if logout fails, clear local state to prevent stuck auth state
      setUserProfile(null);
      setNeedsOnboarding(false);
      throw error;
    }
  };

  // Get Firebase ID token
  const getIdToken = async (): Promise<string | null> => {
    try {
      if (user) {
        return await user.getIdToken();
      }
      return null;
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  };

  // Sync user profile with backend
  const syncUserProfile = async (firebaseUser?: User | null): Promise<UserProfile | null> => {
    const currentFirebaseUser = firebaseUser || user;
    
    // Check if user is authenticated
    if (!currentFirebaseUser) {
      setUserProfile(null);
      setNeedsOnboarding(false);
      return null;
    }

    try {
      setProfileLoading(true);
      console.log('🔄 Syncing user profile with backend...');
      
      const profile = await syncUser();
      setUserProfile(profile);
      
      // Check if user needs onboarding
      const isNewUser = !profile.name || 
                       (!profile.profilePictureUrl && !profile.bio && 
                        (!profile.interests || profile.interests.length === 0));
      
      setNeedsOnboarding(isNewUser);
      
      console.log('✅ User profile synced:', {
        hasProfile: !!profile,
        needsOnboarding: isNewUser,
        profileData: profile
      });
      
      return profile;
    } catch (error) {
      console.error('❌ Error syncing user profile:', error);
      setUserProfile(null);
      setNeedsOnboarding(false);
      return null;
    } finally {
      setProfileLoading(false);
    }
  };

  // Complete onboarding
  const completeOnboarding = (profileData: UserProfile) => {
    setUserProfile(profileData);
    setNeedsOnboarding(false);
    console.log('✅ Onboarding completed successfully!');
  };

  // Check for Google redirect result on app load
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        console.log('🔍 Checking for Google redirect result...');
        const result = await getRedirectResult(auth);
        if (result) {
          console.log('✅ Google redirect sign-in successful:', result.user);
          
          // Determine if this is a new user or returning user
          const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;

          if (isNewUser) {
            trackEvent('registration_success', 'authentication', 'google_redirect');
            trackUserAction('registration_completed', { method: 'google_redirect' });
          } else {
            trackEvent('login_success', 'authentication', 'google_redirect');
            trackUserAction('login_completed', { method: 'google_redirect' });
          }
        } else {
          console.log('ℹ️ No Google redirect result found');
        }
      } catch (error) {
        console.error('❌ Error handling Google redirect result:', error);
        trackEvent('google_auth_failure', 'authentication', 'google_redirect_error');
        trackUserAction('google_auth_failed', { error: error instanceof Error ? error.message : 'unknown' });
      }
    };

    handleRedirectResult();
  }, []);

  // Listen for Firebase authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        console.log('✅ Firebase user signed in:', firebaseUser);
        
        // Check if user is from email/password provider and email is not verified
        const isEmailPasswordUser = firebaseUser.providerData.some(
          provider => provider.providerId === 'password'
        );
        
        if (isEmailPasswordUser && !firebaseUser.emailVerified) {
          console.log('⚠️ Email/password user with unverified email - NOT setting user in state');
          // DON'T set user for unverified email/password users
          // This prevents other components from seeing the user and making API calls
          // The signInWithEmail function will handle signing them out
          setUser(null);
          setUserProfile(null);
          setNeedsOnboarding(false);
        } else {
          // User signed in with verified email or other provider
          setUser(firebaseUser);
          
          // Sync their profile in background
          // Don't block UI render for profile sync
          setTimeout(() => {
            syncUserProfile(firebaseUser);
          }, 100); // Small delay to allow UI to render first
        }
      } else {
        // Firebase user signed out - clear data
        setUser(null);
        setUserProfile(null);
        setNeedsOnboarding(false);
      }
      
      // Set loading to false and mark auth state as stable after a brief delay
      // This prevents flickering by ensuring auth state has settled
      setTimeout(() => {
        setLoading(false);
        setAuthStateStable(true);
      }, 200);
    });

    return unsubscribe;
  }, []);

  // Unified authentication check - user is authenticated if Firebase user exists
  // Only consider authenticated if auth state is stable to prevent flickering
  const isAuthenticated = authStateStable && !!user;

  const value: AuthContextType = {
    user,
    userProfile,
    loading,
    profileLoading,
    needsOnboarding,
    isAuthenticated,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    sendPasswordReset,
    resendVerificationEmail,
    logout,
    getIdToken,
    syncUserProfile,
    completeOnboarding,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
